<script setup lang="ts">
	interface FileContainerProps {
		files: {
			url: string;
			file: File;
		}[];
	}

	const props = defineProps<FileContainerProps>();

	const toast = useToast();

	const input = ref<HTMLInputElement>();

	const maxFiles = 5;
	const error = ref(false);

	const addFile = inject<{
		addFile: (file: File) => void;
	}>('addFile');

	const removeFile = inject<{
		removeFile: (idx: number) => void;
	}>('removeFile');

	const checkForErrors = (filesCount: number) => {
		if (filesCount > maxFiles || props.files.length + filesCount > maxFiles) {
			error.value = true;
			toast.showError(`Не более ${maxFiles} файлов`);
			setTimeout(() => {
				error.value = false;
			}, 3000);
			return true;
		} else {
			error.value = false;
			return false;
		}
	};

	const handleDeleteFile = (idx: number) => {
		checkForErrors(-1);
		removeFile?.removeFile(idx);
	};

	const handleInputFileChange = (elem: HTMLInputElement) => {
		const localFiles = elem.files;

		if (localFiles === null) {
			return;
		}

		if (checkForErrors(localFiles.length)) {
			elem.value = '';
		}
		for (const file of localFiles) {
			addFile?.addFile(file);
		}
	};
</script>

<template>
	<section class="file">
		<input
			ref="input"
			type="file"
			hidden
			accept=".png, .jpeg, .pdf, .mp4, .doc, .docx"
			multiple="true"
			@change="handleInputFileChange($event.target as HTMLInputElement)"
		/>
		<div class="file__header">
			<div v-if="files.length !== 0" class="file__header--right">
				<p
					class="file__header--right__text"
					@click="
						() => {
							input?.click();
						}
					"
				>
					Добавить файл
				</p>
			</div>
		</div>
		<div class="file__body">
			<template v-if="files?.length === 0">
				<div
					class="file__body--empty"
					@click="
						() => {
							input?.click();
						}
					"
				>
					<Icon name="local:folder" />
					<p>Добавьте файл</p>
				</div>
			</template>

			<template v-else>
				<TransitionGroup name="files" tag="ul">
					<FileItem
						v-for="(item, idx) in files"
						:key="idx"
						:item="item"
						@delete="handleDeleteFile(idx)"
					/>
				</TransitionGroup>
			</template>
			<div v-if="error" class="file__error-message">Выберите до 5 файлов</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
	.file {
		position: relative;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
		&__header {
			display: flex;
			align-items: center;
			justify-content: end;

			&--right {
				&__text {
					color: var(--color-grey-dark);
					cursor: pointer;
					transition: color 0.3s ease-in-out;

					&:hover {
						color: var(--color-black);
					}
				}
			}
			&--icon {
				width: 1.5rem;
				height: 1.5rem;
				padding: 0.25rem;
				color: var(--color-white);
				background: var(--color-primary);
				border-radius: 50%;
			}
		}

		&__body {
			&--empty {
				padding: 1.5rem 0;
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 1rem;
				color: var(--color-grey-dark);
				border: dashed 1px var(--color-grey-dark);
				cursor: pointer;
				transition: color 0.3s ease-in-out, border 0.3s ease-in-out;

				&:hover {
					color: var(--color-black);
					border: dashed 1px var(--color-black);
				}

				svg {
					width: 3.375rem;
					height: 3.375rem;
				}

				p {
					max-width: 6.5rem;
					font-size: 0.875rem;
				}
			}
		}
		.file__error-message {
			position: absolute;
			font-size: 12px;
			left: 10px;
			bottom: -1px;
			color: var(--color-error);
		}
	}

	.files-enter-active,
	.files-leave-active {
		transition: all 0.5s ease;
	}
	.files-enter-from,
	.files-leave-to {
		opacity: 0;
		transform: translateX(150px);
	}
</style>
